import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@klaro7/ui';

import { PasswordToggleButton } from '@/components/PasswordInput';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { getDefaultRedirect } from '@/features/auth/context/AuthContext';
import { changePassword } from '@/features/auth/services/auth.service';
import { tokenManager } from '@/lib/api';

const GENERIC_PASSWORD = 'WELCOMEKLARO7';

function validatePassword(pw: string): string | null {
  if (pw.length < 8) return 'La password deve contenere almeno 8 caratteri.';
  if (!/[a-z]/.test(pw)) return 'La password deve contenere almeno una lettera minuscola.';
  if (!/[A-Z]/.test(pw)) return 'La password deve contenere almeno una lettera maiuscola.';
  if (!/[0-9]/.test(pw)) return 'La password deve contenere almeno un numero.';
  if (pw === GENERIC_PASSWORD) return 'Scegli una password diversa da quella generica.';
  return null;
}

export function ChangePasswordPage() {
  const { user, isLoading, isAuthenticated, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user && !user.mustChangePassword) {
    return <Navigate to={getDefaultRedirect(user.role)} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const pwError = validatePassword(newPassword);
    if (pwError) {
      setError(pwError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Le password non coincidono.');
      return;
    }

    setSubmitting(true);
    try {
      const { tokens } = await changePassword({ newPassword, confirmPassword });
      tokenManager.setTokens(tokens);
      await refreshUser();
      navigate(getDefaultRedirect(user!.role), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cambio password fallito. Riprova.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'flex h-11 w-full rounded-lg border border-white/15 bg-white/[0.06] px-3.5 py-2 text-sm text-white placeholder:text-white/30 transition-colors duration-200 focus:border-brand-teal/50 focus:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-brand-teal/20';

  return (
    <div
      className="relative flex min-h-screen items-center justify-center px-4"
      style={{ background: 'linear-gradient(160deg, #231F55 0%, #1a1740 40%, #126f7d 100%)' }}
    >
      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src="/images/logo-clean.png" alt="Klaro7" className="h-14 drop-shadow-lg" />
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-8 shadow-2xl backdrop-blur-xl sm:p-10">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-white">Cambia la tua password</h1>
            <p className="mt-1.5 text-sm text-white/60">
              Per la tua sicurezza imposta una nuova password prima di continuare.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <Alert variant="error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-1.5">
              <label htmlFor="new-password" className="block text-sm font-medium text-white/70">
                Nuova password
              </label>
              <div className="relative">
                <input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Almeno 8 caratteri"
                  required
                  autoComplete="new-password"
                  className={`${inputClass} pr-10`}
                />
                <PasswordToggleButton
                  visible={showNewPassword}
                  onToggle={() => setShowNewPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-white/40 transition-colors hover:text-white/70"
                />
              </div>
              <p className="text-xs text-white/40">Min. 8 caratteri, con maiuscola, minuscola e numero.</p>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="confirm-password" className="block text-sm font-medium text-white/70">
                Conferma password
              </label>
              <div className="relative">
                <input
                  id="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Ripeti la password"
                  required
                  autoComplete="new-password"
                  className={`${inputClass} pr-10`}
                />
                <PasswordToggleButton
                  visible={showConfirmPassword}
                  onToggle={() => setShowConfirmPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-white/40 transition-colors hover:text-white/70"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg disabled:pointer-events-none disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #1899AA 0%, #126f7d 100%)' }}
            >
              {submitting ? 'Salvataggio…' : 'Salva e continua'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
